import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { useTranslation } from '@/hooks/use-translation'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Organization,
  createOrganizationSchema,
  updateOrganizationSchema,
  CreateOrganizationInput,
  UpdateOrganizationInput,
} from './schema'

interface OrganizationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  organization?: Organization | null
  onSubmit: (data: CreateOrganizationInput | UpdateOrganizationInput) => void
  isLoading: boolean
}

export function OrganizationDialog({
  open,
  onOpenChange,
  organization,
  onSubmit,
  isLoading,
}: OrganizationDialogProps) {
  const { t } = useTranslation()
  const isEdit = !!organization

  const form = useForm<CreateOrganizationInput | UpdateOrganizationInput>({
    resolver: zodResolver(
      isEdit ? updateOrganizationSchema : createOrganizationSchema
    ),
    defaultValues: {
      name: '',
      logo: '',
      isEnterprise: false,
    },
  })

  useEffect(() => {
    if (organization) {
      form.reset({
        name: organization.name || '',
        logo: organization.logo || '',
        isEnterprise: organization.isEnterprise || false,
      })
    } else {
      form.reset({
        name: '',
        logo: '',
        isEnterprise: false,
      })
    }
  }, [organization, form])

  const handleSubmit = (
    data: CreateOrganizationInput | UpdateOrganizationInput
  ) => {
    onSubmit(data)
  }

  const handleClose = () => {
    form.reset()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t('organizations.editOrganization')
              : t('organizations.createOrganization')}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? t('organizations.updateOrganizationDetails')
              : t('organizations.fillDetailsToCreate')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className='space-y-4'
          >
            <FormField
              control={form.control}
              name='name'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('organizations.organizationName')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('organizations.enterOrganizationName')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='logo'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('organizations.logoUrl')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('organizations.enterLogoUrl')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='isEnterprise'
              render={({ field }) => (
                <FormItem className='flex flex-row items-start space-x-3 space-y-0'>
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className='space-y-1 leading-none'>
                    <FormLabel>
                      {t('organizations.enterpriseOrganization')}
                    </FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type='button' variant='outline' onClick={handleClose}>
                {t('common.cancel')}
              </Button>
              <Button type='submit' disabled={isLoading}>
                {isLoading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                {isEdit ? t('common.update') : t('common.create')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
